import { useMemo } from 'react';

import { HashLink } from 'react-router-hash-link';

import {
  Link,
  TRoutePathsWithParams,
  useLocale,
} from 'shared/presentation/contexts';
import {
  useGeolocationCoords,
  useTrackedWhatsappLink,
} from 'shared/presentation/hooks';

import * as S from './styles';

type TLink = { children: string } & (
  | { type: 'internal'; to: TRoutePathsWithParams }
  | {
      type: 'external' | 'hash';
      to: string;
      onClick?: () => void;
      openInNewTab?: boolean;
    }
);

const FooterBottomLinks: React.FC = () => {
  const { t } = useLocale('shared');
  const geolocation = useGeolocationCoords();
  const customerServiceTrackedLink = useTrackedWhatsappLink({
    event: 'clicou-botao-whats-rodape',
    message: 'Preciso de atendimento da Vialaser',
  });
  const salesTrackedLink = useTrackedWhatsappLink({
    event: 'clicou-botao-whats-central-vendas',
    message: t(
      "I'm interested in knowing more about laser hair removal promotions",
    ),
  });

  const NAVIGATION_SECTIONS = useMemo<Array<{ title: string; links: TLink[] }>>(
    () => [
      {
        title: 'Menu',
        links: [
          {
            type: 'internal',
            to: '/pacotes',
            children: t('Buy now'),
          },
          {
            type: 'internal',
            to: '/carrinho',
            children: t('Cart'),
          },
          {
            type: 'internal',
            to: '/agendamento',
            children: t('Schedulings'),
          },
          {
            type: 'internal',
            to: '/area-cliente',
            children: t('Customer area'),
          },
        ],
      },
      {
        title: 'Sobre nós',
        links: [
          {
            type: 'internal',
            to: '/quem-somos',
            children: t('Who we are'),
          },
          {
            type: 'external',
            to: 'https://vialaserdepilacao.gupy.io/',
            children: t('Work with us'),
          },
          // {
          //   type: 'external',
          //   to: 'https://vialaser.com.br/franquias',
          //   children: t('Be franchised'),
          // },
          {
            type: 'external',
            to: 'https://www.vialaser.com.br/blog/',
            children: 'Blog',
          },
        ],
      },
      {
        title: 'Ajuda',
        links: [
          {
            type: 'internal',
            to: '/politicas-de-privacidade',
            children: t('Privacy policies'),
          },
          {
            type: 'internal',
            to: '/termos-de-uso',
            children: t('Terms of use'),
          },
          {
            type: 'internal',
            to: geolocation.coords ? '/unidades-proximas' : '/busca-unidades',
            children: t('Where to find'),
          },
          {
            type: 'external',
            to: customerServiceTrackedLink.href,
            onClick: customerServiceTrackedLink.onClickLink,
            openInNewTab: true,
            children: t('Citizen Service'),
          },
          {
            type: 'external',
            to: salesTrackedLink.href,
            onClick: salesTrackedLink.onClickLink,
            openInNewTab: true,
            children: t('Sales center'),
          },
        ],
      },
    ],
    [
      t,
      geolocation.coords,
      customerServiceTrackedLink.href,
      customerServiceTrackedLink.onClickLink,
      salesTrackedLink.href,
      salesTrackedLink.onClickLink,
    ],
  );

  return (
    <S.Container>
      {NAVIGATION_SECTIONS.map(section => (
        <div key={section.title}>
          <h3>{section.title}</h3>

          <ul>
            {section.links.map(link => {
              if (link.type === 'external') {
                return (
                  <a
                    key={`${link.to}-${link.children}`}
                    href={link.to}
                    target={link.openInNewTab ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    onClick={link.onClick}
                  >
                    {link.children}
                  </a>
                );
              }

              if (link.type === 'hash') {
                return (
                  <HashLink key={link.to} to={link.to}>
                    {link.children}
                  </HashLink>
                );
              }

              return (
                <Link key={link.to} to={link.to as TRoutePathsWithParams}>
                  {link.children}
                </Link>
              );
            })}
          </ul>
        </div>
      ))}
    </S.Container>
  );
};

export default FooterBottomLinks;
